/**
 * EditMediaModal.tsx - Main Media Editing Modal Component
 *
 * This component provides a comprehensive media editing interface in a modal
 * with tools, preview, and export capabilities for image and video editing.
 *
 * Key Features:
 * - Full-featured media editing interface
 * - Real-time preview with editing tools
 * - Multiple editing tools and adjustments
 * - Save and export functionality with quality options
 * - Undo/redo history management
 * - Responsive design for different screen sizes
 *
 * Editing Capabilities:
 * - Image cropping with aspect ratio controls
 * - Color adjustments (brightness, contrast, saturation)
 * - Filters and effects application
 * - Resize and resolution changes
 * - Rotation and flip operations
 * - Text overlay and annotation tools
 *
 * Interface Layout:
 * - Left Panel: Editing tools and controls
 * - Center: Media preview with real-time updates
 * - Top Bar: Save, cancel, and export options
 * - Bottom Bar: Zoom controls and view options
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 * - mediaItem: MediaItem - Media item to edit
 * - onSave: (editedMedia: MediaItem) => void - Save changes handler
 *
 * Dependencies: Editing tools, media preview, portal rendering, Canvas API
 */

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { X, RotateCw, FlipHorizontal, FlipVertical } from "lucide-react";
import { ImageEditingTools } from "./ImageEditingTools";
import { EditingPanel, EditingTool } from "./EditingPanel";
import { MediaPreview } from "./MediaPreview";
import { CropTool } from "./CropTool";
import { OpenAIService } from "../../utils/openaiService";
import { MediaType } from "../../enums";
import { MediaModel } from "../../models/GalleryModels";
import {
  ConfigurationStore,
  MediaService,
  OpenAIImagesStore,
  SharedLibraryService,
} from "@mindmemobile/mm-shared-library";
import { AuthService } from "@mindmemobile/mm-shared-global";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../../services";
import { fundraiserStore } from "../../stores";
import ToastService from "../ToastService";
import AIIcon from "../../../../../images/fundraiser/ai.svg";

interface EditMediaModalProps {
  isVisible: boolean;
  mediaItem: MediaModel | null;
  onClose: () => void;
  onSave: (mediaItem: MediaModel) => void;
  showDetailsPanel?: boolean;
}

const ModalOverlay = styled.div<{ preventClose?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
  pointer-events: ${(props) => (props.preventClose ? "none" : "auto")};

  * {
    pointer-events: auto;
  }
`;

const ModalContent = styled.div<{
  showRightPanel?: boolean;
  animate?: boolean;
}>`
  background: #ffffff;
  border-radius: 16px;
  width: ${(props) => (props.showRightPanel ? "1200px" : "800px")};
  max-width: ${(props) => (props.showRightPanel ? "1200px" : "800px")};
  height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  transition: ${(props) =>
    props.animate ? "width 1s ease-in-out, max-width 1s ease-in-out" : "none"};
`;

const ModalHeader = styled.div`
  padding: 24px 32px 16px;
  border-bottom: 1px solid #e5e7eb;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 32px;
  width: 40px;
  height: 40px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  background: #ffffff;
`;

const LeftPanel = styled.div<{ showRightPanel?: boolean; animate?: boolean }>`
  width: ${(props) => (props.showRightPanel ? "calc(60% + 20px)" : "100%")};
  background: #ffffff;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e5e7eb;
  transition: ${(props) => (props.animate ? "width 1s ease-in-out" : "none")};
`;

const IconsRow = styled.div`
  height: 80px;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  position: relative;
  z-index: 1000;
`;

const MediaArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  min-height: 0;
  background: #ffffff;
  padding: 6px 16px 16px 16px;
`;

const RightPanel = styled.div<{ showRightPanel?: boolean; animate?: boolean }>`
  width: ${(props) => (props.showRightPanel ? "calc(40% - 20px)" : "0")};
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  transition: ${(props) => (props.animate ? "width 1s ease-in-out" : "none")};
  opacity: ${(props) => (props.showRightPanel ? 1 : 0)};
  visibility: ${(props) => (props.showRightPanel ? "visible" : "hidden")};
`;

const RightPanelHeader = styled.div`
  height: 80px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
`;

const RightPanelTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const UpdateImageButton = styled.button<{ disabled: boolean }>`
  padding: 12px 20px;
  background: ${(props) => (props.disabled ? "#f3f4f6" : "#ffffff")};
  color: ${(props) => (props.disabled ? "#9ca3af" : "#3b82f6")};
  border: ${(props) =>
    props.disabled ? "1px solid #d1d5db" : "2px solid #3b82f6"};
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  height: 44px;

  &:hover:not(:disabled) {
    background: #f0f9ff;
    border-color: #2563eb;
    color: #2563eb;
  }
`;

const ProgressBarContainer = styled.div<{ visible: boolean }>`
  width: 100%;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
  margin: 16px 0;
  opacity: ${(props) => (props.visible ? 1 : 0)};
  visibility: ${(props) => (props.visible ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

const ProgressBar = styled.div<{ visible: boolean }>`
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 2px;
  animation: ${(props) =>
    props.visible ? "progressSlide 2s infinite" : "none"};
  width: 30%;

  @keyframes progressSlide {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(400%);
    }
  }
`;
const RightPanelContent = styled.div`
  flex: 1;
  padding: 0 20px 0px;
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  padding: 20px 32px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: center;
  gap: 12px;
`;

export const Button = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #3b82f6;
    color: #ffffff;
    
    &:hover {
      background: #2563eb;
    }
  `
      : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}
`;

// Flip options components
const FlipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FlipOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
`;

const FlipLabel = styled.label`
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSwitch = styled.div<{ active: boolean }>`
  width: 48px;
  height: 24px;
  border-radius: 12px;
  background: ${(props) => (props.active ? "#3b82f6" : "#d1d5db")};
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;

  &::after {
    content: "";
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #ffffff;
    position: absolute;
    top: 2px;
    left: ${(props) => (props.active ? "26px" : "2px")};
    transition: all 0.2s ease;
  }
`;

const ResetButton = styled.button`
  padding: 10px 20px;
  background: #ef4444;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;

  &:hover {
    background: #dc2626;
  }
`;

// Rotate options components
const RotateContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
`;

const RotateButton = styled.button`
  padding: 16px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
`;

const RotateButtonLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

export const EditMediaModal: React.FC<EditMediaModalProps> = ({
  isVisible,
  mediaItem,
  onClose,
  onSave,
  showDetailsPanel = true,
}) => {
  const [activeTool, setActiveTool] = useState<EditingTool>("details");
  const [editedItem, setEditedItem] = useState<MediaModel | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [selectedCropPreset, setSelectedCropPreset] = useState("free");
  const [showDelayedCropTool, setShowDelayedCropTool] = useState(false);
  const [cropModified, setCropModified] = useState(false);

  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  // Crop state management
  const [cropData, setCropData] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    shape?: "circle" | "rect";
  } | null>(null);
  const [showCroppedPreview, setShowCroppedPreview] = useState(false);

  // Derive showRightPanel value instead of managing it as state
  const showRightPanel = React.useMemo(() => {
    if (showDetailsPanel) {
      return true; // Always show panel when showDetailsPanel=true
    } else {
      // Show panel only when a design tool is selected when showDetailsPanel=false
      const designTools = [
        "crop",
        "zoom",
        "edit",
        "filters",
        "rotate",
        "flip",
        "ai",
      ];
      return designTools.includes(activeTool);
    }
  }, [showDetailsPanel, activeTool]);

  const configurationStoreRef = useRef<ConfigurationStore | null>(null);

  if (!configurationStoreRef.current) {
    configurationStoreRef.current = new ConfigurationStore({
      userId: AuthService.mindmeAccountDetails?.userId || "",
      subAccountId: AuthService.mindmeAccountDetails?.subAccountId || "",
      parentAccountId: AuthService.mindmeAccountDetails?.parentAccountId || "",
      allowedMediaTypes: ["Image"],
      apiBaseURL: process.env.REACT_APP_MINDME_MAIN_API || "",
      handleAuth: true,
    });
  }

  useEffect(() => {
    const configStore = configurationStoreRef.current;
    if (!configStore) return;

    configStore.updateBearerToken();

    const interval = setInterval(() => {
      if (configStore.bearertoken) {
        clearInterval(interval);
        getCredits();
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const getCredits = async () => {
    const configStore = configurationStoreRef.current;
    if (!configStore) return;
    const mediaService = new MediaService(configStore);
    const response = await firstValueFrom(mediaService.getAICreditPrice());
    if (response?.Data) {
      fundraiserStore.setCredits(
        response.Data.HighQualityCreditsPerImage,
        response.Data.LowQualityCreditsPerImage
      );
    } else {
      fundraiserStore.setIsAICreditsDataNull(true);
    }
  };

  const handleAIEditImage = async (prompt: string) => {
    const imageUrl = editedItem?.fullURL;
    if (!imageUrl) return;

    setIsProcessing(true);

    try {
      await new Promise<void>((resolve, reject) => {
        const configStore = configurationStoreRef.current;
        if (!configStore) {
          reject("Configuration store not initialized.");
          return;
        }

        new OpenAIImagesStore(configStore).openAIService
          .enhanceAnImage(prompt, imageUrl)
          .subscribe({
            next: (response) => {
              if (response?.Data?.Base64) {
                const enhancedImage = new MediaModel({
                  ...editedItem,
                  fullURL: `data:image/png;base64,${response.Data.Base64}`,
                });

                setEditedItem(enhancedImage);
                resolve();
              } else {
                resolve();
              }
            },
            error: (err) => {
              reject(err);
            },
          });
      });
    } catch (error) {
      console.error("Error calling OpenAI service:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // ISSUE FIX #4: Centralized adjustment state for real-time filter updates
  const [adjust, setAdjust] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    blur: 0,
  });

  const handleAdjustChange = (next: Partial<typeof adjust>) => {
    setAdjust((prev) => ({ ...prev, ...next }));
  };

  const handleRotate = (newRotation?: number) => {
    const nextRotation =
      newRotation !== undefined ? newRotation : (rotation + 90) % 360;
    setRotation(nextRotation);
  };

  const handleFlipH = () => {
    setFlipH(!flipH);
  };

  const handleFlipV = () => {
    setFlipV(!flipV);
  };

  const getToolTitle = (activeTool: EditingTool) => {
    switch (activeTool) {
      case "details":
        return "Details";
      case "crop":
        return "Crop";
      case "zoom":
        return "Zoom";
      case "ai":
        return "AI Enhancement";
      case "filters":
        return "Design";
      case "rotate":
        return "Rotate";
      case "flip":
        return "Flip";
      default:
        return "Details";
    }
  };

  // Check if there are any changes to enable/disable save button
  const hasChanges = React.useMemo(() => {
    if (!mediaItem || !editedItem) return false;
    return (
      mediaItem.fullURL !== editedItem.fullURL ||
      flipH ||
      flipV ||
      zoom !== 1 ||
      rotation !== 0 ||
      cropData !== null ||
      adjust.brightness !== 100 ||
      adjust.contrast !== 100 ||
      adjust.saturation !== 100 ||
      adjust.blur !== 0
    );
  }, [mediaItem, editedItem, flipH, flipV, rotation, adjust, cropData, zoom]);

  // Initialize edited item when modal opens
  useEffect(() => {
    if (isVisible && mediaItem) {
      setEditedItem(new MediaModel({ ...mediaItem }));

      // Reset to default state every time modal opens
      if (showDetailsPanel) {
        // Always show panel with details tool when showDetailsPanel=true
        setActiveTool("details");
      } else {
        // Hide panel and reset tool when showDetailsPanel=false
        setActiveTool("details");
      }

      setShowDelayedCropTool(false); // Reset delayed crop tool state
      setZoom(1);
      setPanX(0);
      setPanY(0);
      setFlipH(false);
      setFlipV(false);
      setRotation(0);
      setSelectedCropPreset("free");
    } else if (!isVisible) {
      // Reset states when modal closes
      setShowDelayedCropTool(false);
      setActiveTool("details");
    }
  }, [isVisible, mediaItem, showDetailsPanel]);

  // Handle delayed crop tool rendering when panel needs to animate open
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (activeTool === "crop" && showRightPanel) {
      if (!showDetailsPanel) {
        // Panel needs to animate open, delay crop tool rendering
        timeoutId = setTimeout(() => {
          setShowDelayedCropTool(true);
        }, 1100);
      } else {
        // Panel is already open, show crop tool immediately
        setShowDelayedCropTool(true);
      }
    } else {
      // Not crop tool or panel not open, hide crop tool
      setShowDelayedCropTool(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeTool, showDetailsPanel, showRightPanel]);

  // Update preview mode based on active tool
  useEffect(() => {
    setShowCroppedPreview(cropData !== null && activeTool !== "crop");
  }, [activeTool, cropData]);

  const handleApplyCrop = (newCropData: any) => {
    setCropData(newCropData);
    setActiveTool("details");
    setShowDelayedCropTool(false);
    setShowCroppedPreview(true);
  };

  const handleCancelCrop = () => {
    setActiveTool("details");
    setShowDelayedCropTool(false);
    // Don't change cropData or showCroppedPreview when canceling
  };

  const handleUpdateMediaAndDescription = async (
    media: MediaModel
  ): Promise<boolean> => {
    if (!editedItem) return false;

    try {
      const requestData = {
        fundraiserId: fundraiserStore.selectedFundraiserId,
        mediaId: media.mediaId || "",
        title: editedItem.title || "",
        description: editedItem.description || "",
        accountId: AuthService.authData.accountId || "",
      };

      const response = await firstValueFrom(
        FundraiserService.updateMediaTitleAndDescription(requestData)
      );

      return !!response?.result; // always returns true/false
    } catch (error) {
      return false; // always returns false on error
    }
  };

  const handleSave = async () => {
    if (!editedItem) return;

    let isTextUpdateSuccess = true;
    const titleChanged =
      mediaItem?.title !== editedItem.title ||
      mediaItem?.description !== editedItem.description;

    try {
      setIsProcessing(true);

      if (hasChanges) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = editedItem.fullURL;

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        const canvas = document.createElement("canvas");

        const cropX = (cropData?.x ?? 0) * img.width;
        const cropY = (cropData?.y ?? 0) * img.height;
        const cropWidth = (cropData?.width ?? 1) * img.width;
        const cropHeight = (cropData?.height ?? 1) * img.height;

        canvas.width = cropWidth;
        canvas.height = cropHeight;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.setTransform(1, 0, 0, 1, 0, 0);

        ctx.translate(canvas.width / 2, canvas.height / 2);

        ctx.rotate((rotation * Math.PI) / 180);

        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        ctx.filter = `
  brightness(${adjust.brightness}%)
  contrast(${adjust.contrast}%)
  saturate(${adjust.saturation}%)
  blur(${adjust.blur}px)
`;

        if (cropData?.shape === "circle") {
          const radius = Math.min(canvas.width, canvas.height) / 2;
          ctx.beginPath();
          ctx.arc(0, 0, radius, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
        }

        ctx.drawImage(
          img,
          cropX + (cropWidth * (1 - 1 / zoom)) / 2,
          cropY + (cropHeight * (1 - 1 / zoom)) / 2,
          cropWidth / zoom,
          cropHeight / zoom,
          -cropWidth / 2,
          -cropHeight / 2,
          cropWidth,
          cropHeight
        );

        ctx.restore();

        const newUrl = canvas.toDataURL("image/png");

        if (!configurationStoreRef.current) return;

        await new Promise<void>((resolve, reject) => {
          const configStore = configurationStoreRef.current;
          if (!configStore) {
            reject("Configuration store not initialized.");
            return;
          }

          new MediaService(configStore)
            .saveEnhandedImage("", newUrl)
            .subscribe({
              next: async (response) => {
                if (!response.Data?.FullURL) {
                  reject("Invalid response");
                  return;
                }

                let updatedItem = new MediaModel({
                  ...editedItem,
                  mediaId: response.Data.MediaId,
                  fullURL: response.Data.FullURL,
                  s3Key: response.Data.S3Key,
                });
                if (response.Data.RegularURL) {
                  updatedItem.fullURL = response.Data.RegularURL;
                  updatedItem.s3Key = response.Data.RegularS3Key;
                }
                // ---- If title/description changed, update them ----
                if (titleChanged) {
                  updatedItem = new MediaModel({
                    ...updatedItem,
                    title: editedItem.title,
                    description: editedItem.description,
                  });

                  isTextUpdateSuccess =
                    await handleUpdateMediaAndDescription(updatedItem);
                }

                setEditedItem(updatedItem);
                onSave(updatedItem);
                resolve();
              },
              error: reject,
            });
        });
      } else if (titleChanged && (editedItem.title || editedItem.description)) {
        isTextUpdateSuccess = await handleUpdateMediaAndDescription(editedItem);
        onSave(editedItem);
      }

      onClose();

      if (titleChanged || hasChanges) {
        if (!isTextUpdateSuccess) {
          ToastService.showError("Title and description update failed.");
        } else {
          ToastService.showSuccess("Update Successful");
        }
      }
    } catch (error) {
      console.error("Error generating edited image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFlipReset = () => {
    setAdjust({ brightness: 100, contrast: 100, saturation: 100, blur: 0 });
    setFlipH(false);
    setFlipV(false);
    setRotation(0);
    setCropData(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isVisible || !mediaItem) return null;
  return createPortal(
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent
        showRightPanel={showRightPanel}
        animate={true}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>Edit Media</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <LeftPanel showRightPanel={showRightPanel} animate={true}>
            <IconsRow>
              <ImageEditingTools
                activeTool={activeTool}
                onToolSelect={(tool) => {
                  // Handle tool toggle logic: if same tool clicked again, go back to details
                  if (tool === activeTool && tool !== "details") {
                    setActiveTool("details");
                  } else {
                    setActiveTool(tool);
                  }
                }}
                onCropClick={() => setActiveTool("crop")}
                onRotate={() => handleRotate((rotation || 0) + 90)}
                onFlipH={handleFlipH}
                onFlipV={handleFlipV}
                isProcessing={isProcessing}
                hasValidApiKey={false}
                zoom={zoom}
                onZoomChange={setZoom}
                isVerticalLayout={false}
              />
            </IconsRow>

            <MediaArea>
              {showDelayedCropTool ? (
                <CropTool
                  src={editedItem?.fullURL || mediaItem?.fullURL || ""}
                  selectedCropPreset={selectedCropPreset}
                  onCropPresetChange={setSelectedCropPreset}
                  onApplyCrop={handleApplyCrop}
                  onCancel={handleCancelCrop}
                  onCropModified={setCropModified}
                />
              ) : (
                <MediaPreview
                  src={editedItem?.fullURL || mediaItem?.fullURL || ""}
                  type={editedItem?.type || MediaType.Image}
                  activeTool={activeTool}
                  zoom={zoom}
                  panX={panX}
                  panY={panY}
                  isProcessing={isProcessing}
                  adjust={adjust}
                  rotation={rotation}
                  flipH={flipH}
                  flipV={flipV}
                  cropData={cropData}
                  showCroppedOnly={showCroppedPreview}
                  cropConstrainedZoom={
                    activeTool === "zoom" && cropData !== null
                  }
                />
              )}
            </MediaArea>
          </LeftPanel>

          {showRightPanel && (
            <RightPanel showRightPanel={showRightPanel} animate={true}>
              <RightPanelHeader>
                <RightPanelTitle>{getToolTitle(activeTool)}</RightPanelTitle>
                {activeTool === "ai" && aiPrompt.trim() && (
                  <UpdateImageButton
                    disabled={
                      isProcessing ||
                      !aiPrompt.trim() ||
                      fundraiserStore.isAICreditsDataNull
                    }
                    onClick={() => handleAIEditImage(aiPrompt.trim())}
                  >
                    <img
                      src={AIIcon}
                      alt="AI"
                      width={17}
                      height={17}
                      style={{
                        filter:
                          isProcessing || !aiPrompt.trim()
                            ? "brightness(0) saturate(100%) invert(62%) sepia(8%) saturate(572%) hue-rotate(202deg) brightness(91%) contrast(86%)"
                            : "brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(201deg) brightness(102%) contrast(97%)",
                      }}
                    />

                    {isProcessing ? "Processing..." : "Update"}
                  </UpdateImageButton>
                )}
              </RightPanelHeader>

              <RightPanelContent>
                {activeTool === "ai" && (
                  <ProgressBarContainer visible={isProcessing}>
                    <ProgressBar visible={isProcessing} />
                  </ProgressBarContainer>
                )}
                <EditingPanel
                  activeTool={activeTool}
                  mediaItem={editedItem}
                  adjust={adjust}
                  onAdjustChange={handleAdjustChange}
                  onUpdateMedia={(updates) => {
                    if (editedItem) {
                      setEditedItem(
                        new MediaModel({ ...editedItem, ...updates })
                      );
                    }
                  }}
                  onZoomChange={setZoom}
                  zoom={zoom}
                  onApplyCropPreset={setSelectedCropPreset}
                  selectedCropPreset={selectedCropPreset}
                  onApplyCrop={() => setActiveTool("details")}
                  onCancelCrop={() => setActiveTool("details")}
                  onFlipH={handleFlipH}
                  onFlipV={handleFlipV}
                  onRotate={handleRotate}
                  onRotateChange={setRotation}
                  rotation={rotation}
                  flipH={flipH}
                  flipV={flipV}
                  isProcessing={isProcessing}
                  hasValidApiKey={false}
                  onFlipReset={handleFlipReset}
                  onAIEditImage={handleAIEditImage}
                  aiPrompt={aiPrompt}
                  onAiPromptChange={setAiPrompt}
                  cropData={cropData}
                  cropConstrainedZoom={
                    activeTool === "zoom" && cropData !== null
                  }
                  cropModified={cropModified}
                  onCropApply={() => setActiveTool("details")}
                  onCropReset={() => {
                    setCropModified(false);
                    setSelectedCropPreset("free");
                    setCropData(null);
                    // Stay on crop tool, don't switch to details
                  }}
                />
              </RightPanelContent>
            </RightPanel>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={isProcessing}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
